/**
 * Secure storage utility for handling sensitive data
 * Uses sessionStorage for temporary data and provides secure alternatives to localStorage
 */

// Types for storage keys
export type StorageKey = 
  | 'auth_token'
  | 'user'
  | 'company'
  | 'loginType'
  | 'userRole'
  | 'pendingVerificationEmail'
  | 'pendingVerificationUserId'
  | 'theme';

// Non-sensitive keys that can use localStorage
const NON_SENSITIVE_KEYS: StorageKey[] = ['theme'];

/**
 * Check if a key contains sensitive data
 */
const isSensitiveKey = (key: StorageKey): boolean => {
  return !NON_SENSITIVE_KEYS.includes(key);
};

/**
 * Get data from appropriate storage
 * Uses sessionStorage for sensitive data, localStorage for non-sensitive
 */
export const getSecureItem = (key: StorageKey): string | null => {
  try {
    if (isSensitiveKey(key)) {
      return sessionStorage.getItem(key);
    }
    return localStorage.getItem(key);
  } catch (error) {
    console.error(`Error getting item ${key}:`, error);
    return null;
  }
};

/**
 * Set data in appropriate storage
 * Uses sessionStorage for sensitive data, localStorage for non-sensitive
 */
export const setSecureItem = (key: StorageKey, value: string): void => {
  try {
    if (isSensitiveKey(key)) {
      sessionStorage.setItem(key, value);
    } else {
      localStorage.setItem(key, value);
    }
  } catch (error) {
    console.error(`Error setting item ${key}:`, error);
  }
};

/**
 * Remove data from appropriate storage
 */
export const removeSecureItem = (key: StorageKey): void => {
  try {
    if (isSensitiveKey(key)) {
      sessionStorage.removeItem(key);
    } else {
      localStorage.removeItem(key);
    }
  } catch (error) {
    console.error(`Error removing item ${key}:`, error);
  }
};

/**
 * Clear all sensitive data from storage
 */
export const clearSecureStorage = (): void => {
  try {
    // Clear sessionStorage (contains sensitive data)
    sessionStorage.clear();
    
    // Only clear sensitive items from localStorage
    const sensitiveKeys: StorageKey[] = [
      'auth_token',
      'user',
      'company', 
      'loginType',
      'userRole',
      'pendingVerificationEmail',
      'pendingVerificationUserId'
    ];
    
    sensitiveKeys.forEach(key => {
      localStorage.removeItem(key);
    });
  } catch (error) {
    console.error('Error clearing secure storage:', error);
  }
};

/**
 * Get user data from secure storage
 */
export const getSecureUser = (): any | null => {
  try {
    const userData = getSecureItem('user');
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error('Error getting user data:', error);
    return null;
  }
};

/**
 * Set user data in secure storage
 */
export const setSecureUser = (user: any): void => {
  try {
    setSecureItem('user', JSON.stringify(user));
  } catch (error) {
    console.error('Error setting user data:', error);
  }
};

/**
 * Get auth token from secure storage
 */
export const getAuthToken = (): string | null => {
  return getSecureItem('auth_token');
};

/**
 * Set auth token in secure storage
 */
export const setAuthToken = (token: string): void => {
  setSecureItem('auth_token', token);
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  return !!getAuthToken();
};

/**
 * Get login type from secure storage
 */
export const getLoginType = (): string | null => {
  return getSecureItem('loginType');
};

/**
 * Set login type in secure storage
 */
export const setLoginType = (type: 'user' | 'company'): void => {
  setSecureItem('loginType', type);
};

/**
 * Store pending verification data temporarily
 */
export const setPendingVerification = (email: string, userId: string): void => {
  setSecureItem('pendingVerificationEmail', email);
  setSecureItem('pendingVerificationUserId', userId);
};

/**
 * Clear pending verification data
 */
export const clearPendingVerification = (): void => {
  removeSecureItem('pendingVerificationEmail');
  removeSecureItem('pendingVerificationUserId');
};

/**
 * Get pending verification data
 */
export const getPendingVerification = (): { email: string | null; userId: string | null } => {
  return {
    email: getSecureItem('pendingVerificationEmail'),
    userId: getSecureItem('pendingVerificationUserId')
  };
};
